import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { useSetCampaignDemo } from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

/**
 * Platform-admin-only toggle for a campaign's demo flag (ADR-056), on the Settings → Admin band.
 * Acts on the currently selected campaign. Set/unset is deliberately platform-admin-only — a
 * campaign owner must never be able to self-promote a campaign to demo — so the card is hidden
 * from non-admins and the backend endpoint 403s them as defense-in-depth. On success the mutation
 * invalidates the campaign query, so the DEMO banner appears/disappears in-session without a reload.
 */
export function DemoModeCard() {
  const { user } = useAuth();
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const { mutate: setDemo, isPending } = useSetCampaignDemo();

  if (!user?.isPlatformAdmin || !selectedCampaign) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Demo mode</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Marks this campaign as a demo: shows the “DEMO — data is disposable” banner to all
            members and gates demo-only tooling. Data here is disposable.
          </p>
          <Switch
            checked={selectedCampaign.isDemo}
            onCheckedChange={(checked) =>
              setDemo({ id: selectedCampaign.id, isDemo: checked })
            }
            disabled={isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}
